import { useEffect, useRef } from 'react';

const CHORD_WINDOW_MS = 1000;

/**
 * Register a set of keyboard shortcuts at the document level.
 *
 * Each key in `shortcuts` may be:
 *   - a single key, e.g. 'Escape', 'j', 'Delete', 'Enter'
 *   - a two-key chord separated by a space, e.g. 'g n' (press g, then n
 *     within 1000ms)
 *
 * Matching is case-insensitive on letter keys. Special keys (Escape, Enter,
 * Backspace, Delete, Tab, Arrow*, Home, End, PageUp/Down) are matched by
 * `event.key` directly with that exact casing.
 *
 * Skips events whose target is an <input>, <textarea>, <select>,
 * contenteditable element, or any element with role="textbox", so users can
 * type freely inside forms without firing shortcuts.
 *
 * @param {Record<string, (event: KeyboardEvent) => void>} shortcuts
 * @param {{ enabled?: boolean }} [options]
 */
export function useKeyboardShortcuts(shortcuts, options = {}) {
  const { enabled = true } = options;
  const shortcutsRef = useRef(shortcuts);
  useEffect(() => { shortcutsRef.current = shortcuts; }, [shortcuts]);

  useEffect(() => {
    if (!enabled) return undefined;

    let pendingPrefix = null;       // first key of a chord
    let pendingTimer = null;

    const clearPending = () => {
      pendingPrefix = null;
      if (pendingTimer) {
        clearTimeout(pendingTimer);
        pendingTimer = null;
      }
    };

    const onKeyDown = (event) => {
      if (isEditable(event.target)) return;
      // Ignore modifier-combos so browser/OS shortcuts continue to work.
      if (event.ctrlKey || event.metaKey || event.altKey) return;

      const key = normalizeKey(event.key);
      const map = shortcutsRef.current || {};

      // Try resolving a pending chord first.
      if (pendingPrefix) {
        const chord = `${pendingPrefix} ${key}`;
        clearPending();
        const handler = findHandler(map, chord);
        if (handler) {
          event.preventDefault();
          handler(event);
          return;
        }
        // Chord didn't resolve — fall through and treat this key as a fresh
        // single-key press.
      }

      // Single-key match?
      const single = findHandler(map, key);
      if (single) {
        event.preventDefault();
        single(event);
        return;
      }

      // Could this be the first key of a chord?
      const isChordPrefix = Object.keys(map).some((seq) => {
        const parts = seq.split(/\s+/);
        return parts.length === 2 && normalizeKey(parts[0]) === key;
      });
      if (isChordPrefix) {
        pendingPrefix = key;
        pendingTimer = setTimeout(clearPending, CHORD_WINDOW_MS);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      clearPending();
    };
  }, [enabled]);
}

function normalizeKey(key) {
  if (!key) return '';
  // Letters/digits → lowercase. Named keys ("Escape", "Enter", ...) keep
  // their original casing.
  if (key.length === 1) return key.toLowerCase();
  return key;
}

function findHandler(map, seq) {
  // First try exact match, then a case-insensitive sweep across the map keys
  // (so callers can declare 'g n' or 'G N' interchangeably).
  if (typeof map[seq] === 'function') return map[seq];
  for (const declared of Object.keys(map)) {
    const norm = declared.split(/\s+/).map(normalizeKey).join(' ');
    if (norm === seq && typeof map[declared] === 'function') return map[declared];
  }
  return null;
}

function isEditable(target) {
  if (!target || target.nodeType !== 1) return false;
  const el = /** @type {HTMLElement} */ (target);
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (el.isContentEditable) return true;
  const role = el.getAttribute && el.getAttribute('role');
  if (role === 'textbox') return true;
  return false;
}
